import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { Helmet } from 'react-helmet';
import { MdOutlineArrowBack } from 'react-icons/md';
import GaugeComponent from 'react-gauge-component';
import moment from 'moment';
import axios from 'config/axios';
import { setError } from 'store/error';
import ButtonLink from 'components/ButtonLink';
import LevelBar from 'components/LevelBar';
import ScoreWithScale from 'components/ScoreWithScale2';
import useWindowSize from 'hooks/useWindowSize';
import { MULTIPLE_CHOICES } from 'global/constants';

import './index.css';

const timePerQuestionEnglish = 35 * 60 / 50;
const timePerQuestionMath = 70 * 60 / 44;    
const timePerQuestionReading = 40 * 60 / 36;
const timePerQuestionScience = 40 * 60 / 36;

function DigitalACTReport() {
    const [barWidth, setBarWidth] = useState(0);
    const [fontSize, setFontSize] = useState(80);
    const [testData, setTestData] = useState(null);
    const [sectionIndex, setSectionIndex] = useState(0);
    const [moduleIndex, setModuleIndex] = useState(0);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { organization } = useAppSelector(state => state.organization);
    const { testId } = useParams();
    const barRef = useRef(null);

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const formatTime2 = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return hours 
            ? `${hours} hr ${minutes} min ${seconds} sec`
            : minutes
            ? `${minutes} min ${seconds} sec`
            : `${seconds} sec`;
    }

    useEffect(() => {
        axios.get(`tests/digital/result/act/${testId}/`)
            .then(res => {
                setTestData(res.data);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }, []);

    useEffect(() => {
        const updateLayout = () => {
            if (barRef.current) {
                setBarWidth(barRef.current.offsetWidth);
            }
            const width = window.innerWidth;
            if (width > 1300) {
                setFontSize(80);
            } else {
                setFontSize(60);
            }
        };
    
        updateLayout(); // 초기 실행
        window.addEventListener('resize', updateLayout);
        return () => {
            window.removeEventListener('resize', updateLayout);
        };
    }, []);

    useLayoutEffect(() => {
        if (barRef.current) {
            setBarWidth(barRef.current.offsetWidth);
        }
    }, [testData]);

    if (!testData) return <></>;

    return (
        <div className='layout'>
            <Helmet>
                <title>
                    {testData.name} Report - {organization.name}
                </title>
            </Helmet>
            <div className='digitalACTReport__bar'>
                <button
                    className='digitalACTReport__back'
                    // onClick={() => testData.testType === 'Digital SAT'
                    //     ? navigate('/tests/digital-sat')
                    //     : testData.testType === 'Digital PSAT'
                    //     ? navigate('/tests/digital-psat')
                    //     : testData.testType === 'Digital ACT'
                    //     ? navigate('/tests/digital-act')
                    //     : navigate('/tests')
                    // }
                    onClick={() => navigate('/tests')}
                >
                    <MdOutlineArrowBack className='digitalACTReport__backIcon' />
                    Back to My Tests
                </button>
            </div>
            <div className='digitalACTReport__headerGroup'>
                <div className='digitalACTReport__headerSection'>
                    <img
                        className='digitalACTReport__headerLogo'
                        alt='logo'
                        src={testData.logo}
                    />
                </div>
                <div className='digitalACTReport__headerSection'>
                    <div className='digitalACTReport__headerInfo'>
                        <p>
                            Name: {testData.studentName}
                        </p>
                        <p>
                            Test name: {testData.name}
                        </p>
                        <p>
                            Tested on: {moment(testData.testDate).format('MMMM D YYYY')}
                        </p>
                    </div>
                </div>
            </div>
            <div className='digitalACTReport__headerGroup'>
                <h1 className='digitalACTReport__headerTitle'>
                    Test Result Overview
                </h1>
                <div className='digitalACTReport__buttonGroup'>
                    <ButtonLink className='digitalACTReport__button' to={`/review/digital-act/${testId}`}>
                        REVIEW QUESTIONS
                    </ButtonLink>
                </div>
            </div>
            <div className='digitalACTReport__card'>
                {/* test scores */}
                <div className='digitalACTReport__scoresGroup'>
                    {testData.compositeScore && <>
                        <div className='digitalACTReport__scoresGroupBox'>
                            <div className='digitalACTReport__scoreTitleBoxCom'>
                                <h3 className='digitalACTReport__scoreTitle'>COMPOSITE SCORE</h3>
                            </div>
                            <div className='digitalACTReport__scoreContent'>
                                <ScoreWithScale
                                    className='digitalACTReport__scoreWithScale'
                                    score={Math.round(testData.compositeScore)}
                                    scoreMin={Math.round(testData.minScore)}
                                    scoreMax={Math.round(testData.maxScore)}
                                    fontSize={70}
                                    // fontSize={windowSize.width > 576
                                    //     ? 100
                                    //     : 70
                                    // }
                                />
                                <div className="digitalACTReport__graphWrap">
                                    <div className="digitalACTReport__graphLocation" style={{ left: `${((Math.round(testData.compositeScore) - 1) / 35) * barWidth}px` }}>
                                        <div className="digitalACTReport__graph">you</div>
                                        <div className="triangle"></div>
                                    </div>
                                    <div className="digitalACTReport__graphBar" ref={barRef}/>
                                    <div className="digitalACTReport__maxScore">
                                        {Math.round(testData.maxScore)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>}
                    {testData.stemScore && <>
                        <div className='digitalACTReport__scoresGroupBoxStem'>
                            <div className='digitalACTReport__scoreTitleBoxStem'>
                                <h3 className='digitalACTReport__scoreTitle'>STEM SCORE</h3>
                            </div>
                            <div className='digitalACTReport__scoreContent'>
                                <ScoreWithScale
                                    className='digitalACTReport__scoreWithScale'
                                    score={Math.round(testData.stemScore)}
                                    scoreMin={Math.round(testData.minScore)}
                                    scoreMax={Math.round(testData.maxScore)}
                                    fontSize={70}
                                    // fontSize={windowSize.width > 576
                                    //     ? 100
                                    //     : 70
                                    // }
                                />
                                <div className="digitalACTReport__graphWrap">
                                    <div className="digitalACTReport__graphLocation" style={{ left: `${((Math.round(testData.stemScore) - 1) / 35) * barWidth}px` }}>
                                        <div className="digitalACTReport__graph">you</div>
                                        <div className="triangle"></div>
                                    </div>
                                    <div className="digitalACTReport__graphBarStem" />
                                    <div className="digitalACTReport__maxScore">
                                        {Math.round(testData.maxScore)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>}
                </div>

                {/* subject info */}
                {testData.testSections.map((testSection, s) => {
                    const testModule = testData.testModules.find(module => module.subject === testSection.subject);
                    const correctCount = testModule ? testModule.answers.filter(answer => answer.isCorrect).length : '-';
                    const totalCount = testModule ? testModule.answers.length : '-';
                    const timeSpent = testModule ? testModule.answers.reduce((total, answer) => total + answer.timeSpent, 0) : null;

                    return (
                        <div className='digitalACTReport__insideCard'>
                            <div className='digitalACTReport__ScoreBox'>
                                <div className='digitalACTReport__sectionScore'>
                                    <h3 className='digitalACTReport__sectionScoreTitle'>
                                        {testSection.subject.replace('ACT ', '').replace('Mathematics', 'Math')}
                                    </h3>
                                    <ScoreWithScale
                                        className='digitalACTReport__scoreWithScale'
                                        score={Math.round(testSection.score)}
                                        scoreMin={Math.round(testSection.minScore)}
                                        scoreMax={Math.round(testSection.maxScore)}
                                        fontSize={fontSize}
                                    />
                                </div>
                                <div className='digitalACTReport__rowScore'>
                                    <div className='digitalACTReport__infoTitle'>Raw score</div>
                                    <table className='digitalACTReport__overviewTable'>
                                        <tbody>
                                            <tr>
                                                <th>Correct</th>
                                                <td>{correctCount}/{totalCount}</td>
                                            </tr>
                                            <tr>
                                                <th>Time</th>
                                                <td>{timeSpent !== null ? formatTime(timeSpent) : '-'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className='digitalACTReport__sectionInfo'>
                                <table className='digitalACTReport__categoryInfo'>
                                    <tr>
                                        <th>By Category</th>
                                        <th>Section Time</th>
                                        <th>Time Per Question</th>
                                    </tr>
                                    {testSection.rootCategories.map(rootCategory => (<>
                                        <tr>
                                            <td>
                                                <h5 className='digitalACTReport__levelLabel'>
                                                    {rootCategory.name}
                                                </h5>
                                                <LevelBar
                                                    className={testSection.subject==='ACT English'
                                                        ? 'LevelBar__greenBox'
                                                        : testSection.subject==='ACT Mathematics'
                                                        ? 'LevelBar__orangeBox'
                                                        : testSection.subject==='ACT Reading'
                                                        ? 'LevelBar__redBox'
                                                        : 'LevelBar__blueBox'
                                                        } 
                                                    percent={100*rootCategory.numCorrect/rootCategory.numQuestions}
                                                    label={`${rootCategory.numCorrect}/${rootCategory.numQuestions}`}
                                                />
                                            </td>
                                            <td>{formatTime2(rootCategory.timeSpent)}</td>
                                            <td>
                                                <GaugeComponent
                                                    className='digitalACTReport__timeGauge'
                                                    type='semicircle'
                                                    value={rootCategory.timeSpent/rootCategory.numQuestions}
                                                    minValue={0}
                                                    maxValue={testSection.subject === 'ACT English'
                                                        ?   timePerQuestionEnglish * 3
                                                        :   testSection.subject === 'ACT Mathematics'
                                                        ?   timePerQuestionMath * 3
                                                        :   testSection.subject === 'ACT Reading'
                                                        ?   timePerQuestionReading * 3
                                                        :   testSection.subject === 'ACT Science'
                                                        ?   timePerQuestionScience * 3
                                                        :   180
                                                    }
                                                    arc={{
                                                        'subArcs': [
                                                            { length: 0.33, color: "#5BE12C"},
                                                            { length: 0.33, color: "#F5CD19"},
                                                            { length: 0.33, color: "#EA4228"}
                                                        ]
                                                    }}
                                                    pointer={{
                                                        'width': 10,
                                                    }}
                                                    labels={{
                                                        'valueLabel': {
                                                            'hide': true
                                                        },
                                                        'tickLabels': {
                                                            'hideMinMax': true
                                                        }
                                                    }}
                                                />
                                                <p>{formatTime2(rootCategory.timeSpent/rootCategory.numQuestions)}</p>
                                            </td>
                                        </tr>
                                    </>))}
                                </table>
                            </div>
                        </div>
                    )
                })}
            </div>
            <div className='digitalACTReport__questions'>
                <h2 className='digitalACTReport__questionsHeader'>
                    Questions Overview
                </h2>
                <div className='digitalACTReport__sectionTabs'>
                    {testData.testSections.map((testSection, t) => (
                        <button
                            className={sectionIndex === t
                                ? 'digitalACTReport__sectionTab--active'
                                : 'digitalACTReport__sectionTab'
                            }
                            onClick={() => {
                                setSectionIndex(t);
                                setModuleIndex(0);
                            }}
                            key={testSection.subject}
                        >
                            {testSection.subject.replace('ACT ','')}
                        </button>
                    ))}
                    <div className={sectionIndex === 0
                        ? 'digitalACTReport__sectionTabIndicatorLeft'
                        : sectionIndex === 1
                        ? 'digitalACTReport__sectionTabIndicatorMiddle'
                        : sectionIndex === 2
                        ? 'digitalACTReport__sectionTabIndicatorMiddle2'
                        : 'digitalACTReport__sectionTabIndicatorRight'
                    }></div>
                </div>
                {testData.testModules.filter(testModule => (
                    testModule.subject === testData.testSections[sectionIndex].subject
                )).length > 1 &&
                    <div className='digitalACTReport__moduleTabs'>
                        {testData.testModules.filter(testModule => (
                            testModule.subject === testData.testSections[sectionIndex].subject
                        )).map((testModule, t) => (
                            <button
                                className={moduleIndex === t
                                    ? 'digitalACTReport__moduleTab--active'
                                    : 'digitalACTReport__moduleTab'
                                }
                                onClick={() => setModuleIndex(t)}
                                key={testModule.name}
                            >
                                Module {testModule.moduleNumber} {testModule.moduleNumber === 2 && (testModule.hard ? ' (Hard)' : ' (Easy)')}
                            </button>
                        ))}
                        <div className={moduleIndex === 0
                            ? 'digitalACTReport__moduleTabIndicatorLeft'
                            : 'digitalACTReport__moduleTabIndicatorRight'
                        }></div>
                    </div>
                }
                <div className='digitalACTReport__questionsOverview'>
                    <div className='digitalACTReport__questionsSummary'>
                        <div className='digitalACTReport__questionsSummaryItem'>
                            <p className='digitalACTReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.length
                                ), 0)}
                            </p>
                            <p className='digitalACTReport__questionSummaryText'>
                                Total Questions
                            </p>
                        </div>
                        <div className='digitalACTReport__questionsSummaryItem'>
                            <p className='digitalACTReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.filter(answer => answer.isCorrect).length
                                ), 0)}
                            </p>
                            <p className='digitalACTReport__questionSummaryText'>
                                Correct Answers
                            </p>
                        </div>
                        <div className='digitalACTReport__questionsSummaryItem'>
                            <p className='digitalACTReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.filter(answer => !answer.isCorrect).length
                                ), 0)}
                            </p>
                            <p className='digitalACTReport__questionSummaryText'>
                                Incorrect Answers
                            </p>
                        </div>
                    </div>
                    <table className='digitalACTReport__questionsTable'>
                        <tbody>
                            <tr>
                                <th>Question</th>
                                {organization.customizations.find(customization => customization === 'Enable Show Answer') && <th>Correct Answer</th>}
                                <th>Your Answer</th>
                                <th>Time Spent</th>
                            </tr>
                            {(() => {
                                const currentModule = testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                ))[moduleIndex];
                                return currentModule.answers.map(answer => (
                                <tr key={currentModule.name + answer.questionNumber}>
                                    <td>{answer.questionNumber}</td>
                                    {organization.customizations.find(customization => customization === 'Enable Show Answer') &&
                                        <td>{answer.correctChoice !== null
                                            ?   MULTIPLE_CHOICES[answer.correctChoice]
                                            :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                        }</td>
                                    }
                                    <td>{answer.answer !== null
                                        ?   (answer.correctAnswers.includes(answer.answer)
                                                ? <p>{answer.answer}; Correct</p>
                                                : <p className='digitalACTReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                            )
                                        :   answer.choice !== null
                                        ?   (answer.choice === answer.correctChoice
                                                ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                : <p className='digitalACTReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                            )
                                        :   <p className='digitalACTReport__questionOmitted'>Omitted</p>
                                    }</td>
                                    <td>
                                        <p className={answer.timeSpent > (currentModule.subject === 'ACT English'
                                                    ? timePerQuestionEnglish * 1.5
                                                    : currentModule.subject === 'ACT Mathematics'
                                                    ? timePerQuestionMath * 1.5
                                                    : currentModule.subject === 'ACT Reading'
                                                    ? timePerQuestionReading * 1.5
                                                    : currentModule.subject === 'ACT Science'
                                                    ? timePerQuestionScience * 1.5
                                                    : 60 * 1.5
                                                )
                                            ?   'digitalACTReport__timeSpentBad'
                                            :   answer.timeSpent > (currentModule.subject === 'ACT English'
                                                    ? timePerQuestionEnglish
                                                    : currentModule.subject === 'ACT Mathematics'
                                                    ? timePerQuestionMath
                                                    : currentModule.subject === 'ACT Reading'
                                                    ? timePerQuestionReading
                                                    : currentModule.subject === 'ACT Science'
                                                    ? timePerQuestionScience
                                                    : 60
                                                )
                                            ?   'digitalACTReport__timeSpentWarning'
                                            :   'digitalACTReport__timeSpentGood'
                                        }>
                                            {formatTime(answer.timeSpent)}
                                        </p>
                                    </td>
                                </tr>
                            ));
                            })()}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default DigitalACTReport;